import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TvIcon from '@mui/icons-material/Tv';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import Brightness5Icon from '@mui/icons-material/Brightness5';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { Helmet } from 'react-helmet-async';
import TVFrameCard from './TVFrameCard';
import TVFrameLabel from './TVFrameLabel';
import TVIntroPanel from './TVIntroPanel';
import TVStationTodayPanel from './TVStationTodayPanel';
import TVStationWeekPanel from './TVStationWeekPanel';
import TVPastWeekOverviewPanel from './TVPastWeekOverviewPanel';
import TVActionPanel from './TVActionPanel';
import TVWeeklyIntroPanel from './TVWeeklyIntroPanel';
import TVPreviousWeekPanel from './TVPreviousWeekPanel';
import TVNextWeekPanel from './TVNextWeekPanel';
import TVWebGuidePanel from './TVWebGuidePanel';
import {
  getTvSequenceForMode,
  getTvSequenceTotalSeconds,
  shouldShowTvFrameQr,
  isTvActionFrame,
} from '../../data/tvFrameLabels';
import {
  TV_BROADCAST_DEFAULT_PERIOD,
  TV_BROADCAST_PERIOD_OPTIONS,
  TV_BROADCAST_PERIODS,
} from '../../data/tvBroadcastPeriod';
import {
  TV_BROADCAST_DEFAULT_MODE,
  TV_BROADCAST_MODE_OPTIONS,
  TV_BROADCAST_MODES,
} from '../../data/tvBroadcastMode';
import useTvDemoData from '../../hooks/useTvDemoData';
import tvTheme, { tvGraphPaperBg } from './tvTheme';

const MODES = {
  STORYBOARD: 'storyboard',
  PREVIEW: 'preview',
};

const PERIOD_ICONS = {
  [TV_BROADCAST_PERIODS.MORNING]: WbSunnyIcon,
  [TV_BROADCAST_PERIODS.NOON]: Brightness5Icon,
  [TV_BROADCAST_PERIODS.EVENING]: NightsStayIcon,
};

function TVStoryboardPage() {
  const [mode, setMode] = useState(MODES.STORYBOARD);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [broadcastMode, setBroadcastMode] = useState(TV_BROADCAST_DEFAULT_MODE);
  const [broadcastPeriod, setBroadcastPeriod] = useState(TV_BROADCAST_DEFAULT_PERIOD);

  const { loading, error, data, isMock, dataSource, thresholdProfile } = useTvDemoData(
    broadcastPeriod,
    broadcastMode,
  );
  const isWeekly = broadcastMode === TV_BROADCAST_MODES.WEEKLY;

  const activeSequence = useMemo(
    () => getTvSequenceForMode(broadcastMode, broadcastPeriod),
    [broadcastMode, broadcastPeriod],
  );

  const totalSeconds = useMemo(
    () => getTvSequenceTotalSeconds(activeSequence),
    [activeSequence],
  );

  const goPrev = useCallback(() => {
    setPreviewIndex((i) => (i <= 0 ? activeSequence.length - 1 : i - 1));
  }, [activeSequence.length]);

  const goNext = useCallback(() => {
    setPreviewIndex((i) => (i >= activeSequence.length - 1 ? 0 : i + 1));
  }, [activeSequence.length]);

  useEffect(() => {
    setPreviewIndex(0);
  }, [broadcastMode, broadcastPeriod]);

  useEffect(() => {
    setPreviewIndex((i) => Math.min(i, Math.max(0, activeSequence.length - 1)));
  }, [activeSequence.length]);

  useEffect(() => {
    if (mode !== MODES.PREVIEW) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, goPrev, goNext]);

  const renderFrameContent = useCallback(
    (frame, compact) => {
      if (!data) {
        return (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 700, color: tvTheme.colors.textDark }}>
              {loading ? 'データ読込中…' : 'データがありません'}
            </Typography>
          </Box>
        );
      }

      switch (frame.kind) {
        case 'weeklyIntro':
          return <TVWeeklyIntroPanel data={data.intro} compact={compact} />;
        case 'previousWeek':
          return (
            <TVPreviousWeekPanel data={data.previousWeek} compact={compact} loading={loading} />
          );
        case 'nextWeek':
          return <TVNextWeekPanel data={data.nextWeek} compact={compact} loading={loading} />;
        case 'webGuide':
          return <TVWebGuidePanel data={data.webGuide} compact={compact} loading={loading} />;
        default:
          break;
      }

      const { intro, spots, action, meta } = data;
      const spotPanels = spots?.[frame.spotId];

      switch (frame.kind) {
        case 'intro':
          return <TVIntroPanel data={intro} meta={meta} compact={compact} />;
        case 'spotToday':
          return <TVStationTodayPanel data={spotPanels?.today} compact={compact} loading={loading} />;
        case 'spotTomorrow':
          return (
            <TVStationTodayPanel data={spotPanels?.tomorrow} compact={compact} loading={loading} />
          );
        case 'spotWeek':
          return <TVStationWeekPanel data={spotPanels?.week} compact={compact} loading={loading} />;
        case 'todayOverview':
          return (
            <TVPastWeekOverviewPanel
              data={data.todayOverview}
              compact={compact}
              loading={loading}
            />
          );
        case 'yesterdayOverview':
          return (
            <TVPastWeekOverviewPanel
              data={data.yesterdayOverview}
              compact={compact}
              loading={loading}
            />
          );
        case 'action':
          return <TVActionPanel data={action} compact={compact} loading={loading} />;
        default:
          return null;
      }
    },
    [data, loading],
  );

  const flowHint = isWeekly
    ? '週1更新・過去1週間の実績と今後1週間の予測（会議比較用プロトタイプ）'
    : broadcastPeriod === TV_BROADCAST_PERIODS.EVENING
      ? '夜：今日→明日→週間（4エリア→駅前→古い町並み）'
      : '朝・昼：昨日→今日→週間（4エリア→駅前・今日→古い町並み・今日→週間）';

  const frames = useMemo(
    () =>
      activeSequence.map((frame) => ({
        ...frame,
        content: renderFrameContent(frame, mode === MODES.STORYBOARD),
      })),
    [activeSequence, renderFrameContent, mode],
  );

  const frameCountLabel = String(frames.length);
  const periodLabel = TV_BROADCAST_PERIOD_OPTIONS.find((o) => o.id === broadcastPeriod)?.label;
  const modeLabel = TV_BROADCAST_MODE_OPTIONS.find((o) => o.id === broadcastMode)?.label;

  const footerFlow = isWeekly
    ? '導入（1）→ 先週の計測（2）→ 今週の予測（3）→ Web案内（4） · QRはWeb案内のみ'
    : broadcastPeriod === TV_BROADCAST_PERIODS.EVENING
      ? '導入（1）→ 今日の流れ（2）→ 駅前・明日（3）→ 古い町並み・明日（4）→ 駅前・週間（5）→ 古い町並み・週間（6）→ 行動提案（7）'
      : '導入（1）→ きのうの流れ（2）→ 駅前・今日（3）→ 古い町並み・今日（4）→ 駅前・週間（5）→ 古い町並み・週間（6）→ 行動提案（7）';

  return (
    <>
      <Helmet>
        <title>テレビ向け人通り予報デモ | 高山市人流ダッシュボード</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Box
        className={`tv-demo-page${mode === MODES.PREVIEW ? ' tv-demo-page--preview' : ''}`}
        sx={{
          minHeight: '100vh',
          ...(mode === MODES.PREVIEW ? tvGraphPaperBg : { background: tvTheme.colors.skyGradient }),
          color: tvTheme.colors.textDark,
          py: 2,
          px: { xs: 1.5, md: 3 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: mode === MODES.PREVIEW ? tvTheme.colors.textDark : '#fff',
                  textShadow: mode === MODES.PREVIEW ? 'none' : '0 1px 4px rgba(13,74,143,0.5)',
                }}
              >
                テレビ向け人通り予報デモ
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: tvTheme.colors.textDark, fontWeight: 600 }}>
                {modeLabel} · {flowHint}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={loading ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : undefined}
                label={
                  loading
                    ? '更新中'
                    : isWeekly
                      ? `${dataSource === 'live' ? 'live' : 'mock'}${dataSource === 'live' ? ` · ${thresholdProfile}` : ''}`
                      : isMock
                        ? 'モック'
                        : '実データ'
                }
                size="small"
                sx={{ bgcolor: '#fff', color: tvTheme.colors.accentBlue, fontWeight: 700 }}
              />
              <ToggleButtonGroup
                exclusive
                value={mode}
                onChange={(_, v) => v && setMode(v)}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  borderRadius: 1,
                  '& .MuiToggleButton-root': {
                    color: tvTheme.colors.textDark,
                    borderColor: 'rgba(13,74,143,0.2)',
                    fontWeight: 700,
                    '&.Mui-selected': {
                      bgcolor: tvTheme.colors.labelBar,
                      color: '#fff',
                    },
                  },
                }}
              >
                <ToggleButton value={MODES.STORYBOARD}>
                  <GridViewIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  一覧
                </ToggleButton>
                <ToggleButton value={MODES.PREVIEW}>
                  <TvIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  単画面
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.92)',
              border: '3px solid #fff',
              boxShadow: '0 4px 16px rgba(13,74,143,0.15)',
            }}
          >
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: tvTheme.colors.textDark }}>
              放送案
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={broadcastMode}
              onChange={(_, v) => v && setBroadcastMode(v)}
              size="small"
              sx={{
                bgcolor: '#fff',
                borderRadius: 1,
                '& .MuiToggleButton-root': {
                  px: 2,
                  fontWeight: 800,
                  color: tvTheme.colors.textDark,
                  borderColor: 'rgba(13,74,143,0.25)',
                  '&.Mui-selected': {
                    bgcolor: tvTheme.colors.labelBar,
                    color: '#fff',
                  },
                },
              }}
            >
              {TV_BROADCAST_MODE_OPTIONS.map(({ id, label }) => (
                <ToggleButton key={id} value={id}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {isWeekly && (
              <Typography sx={{ fontSize: '0.8rem', color: tvTheme.colors.textMuted, fontWeight: 600 }}>
                目安尺 {totalSeconds}秒（{frameCountLabel}画面） · ← → で順送り · QRはWeb案内のみ
              </Typography>
            )}

            {!isWeekly && (
              <>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: tvTheme.colors.textDark }}>
                  放送時間帯
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={broadcastPeriod}
                  onChange={(_, v) => v && setBroadcastPeriod(v)}
                  size="small"
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 1,
                    '& .MuiToggleButton-root': {
                      px: 2,
                      fontWeight: 800,
                      color: tvTheme.colors.textDark,
                      borderColor: 'rgba(13,74,143,0.25)',
                      '&.Mui-selected': {
                        bgcolor: tvTheme.colors.labelBar,
                        color: '#fff',
                      },
                    },
                  }}
                >
                  {TV_BROADCAST_PERIOD_OPTIONS.map(({ id, label }) => {
                    const Icon = PERIOD_ICONS[id];
                    return (
                      <ToggleButton key={id} value={id}>
                        <Icon sx={{ mr: 0.5, fontSize: 18 }} />
                        {label}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: '0.8rem', color: tvTheme.colors.textMuted, fontWeight: 600 }}>
                  {periodLabel}の予報 · ← → で{frameCountLabel}画面を順送り · 導入は本町4丁目固定
                </Typography>
              </>
            )}
          </Box>

          {error && (
            <Alert
              severity={error.includes('live') || error.includes('[develop]') ? 'error' : 'warning'}
              sx={{ fontWeight: 600 }}
            >
              {error}
            </Alert>
          )}
        </Box>

        {mode === MODES.STORYBOARD && (
          <Box
            sx={{
              maxWidth: 1400,
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            {frames.map((frame, index) => (
              <Box key={frame.key || frame.id}>
                <TVFrameLabel
                  frameNumber={frame.id}
                  frameTitle={
                    frame.durationSeconds
                      ? `${frame.title}（${frame.durationSeconds}秒）`
                      : frame.title
                  }
                />
                <TVFrameCard
                  compact
                  showBadge={false}
                  showQrOverlay={
                    // weekly は webGuide パネル内に大きく表示（右上オーバーレイは使わない）
                    !isWeekly && shouldShowTvFrameQr(frame, broadcastMode)
                  }
                  onClick={() => {
                    setPreviewIndex(index);
                    setMode(MODES.PREVIEW);
                  }}
                >
                  {frame.content}
                </TVFrameCard>
              </Box>
            ))}
          </Box>
        )}

        {mode === MODES.PREVIEW && frames[previewIndex] && (
          <Box
            sx={{
              maxWidth: 'min(1600px, 98vw)',
              mx: 'auto',
              width: '100%',
              ...tvGraphPaperBg,
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <IconButton
                onClick={goPrev}
                sx={{ color: tvTheme.colors.textDark, bgcolor: 'rgba(255,255,255,0.9)', border: '2px solid #ccc' }}
                aria-label="前の画面"
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 800,
                  minWidth: 220,
                  textAlign: 'center',
                  color: tvTheme.colors.textDark,
                }}
              >
                画面 {frames[previewIndex].id} / {frames.length}
                {frames[previewIndex].durationSeconds
                  ? ` · ${frames[previewIndex].durationSeconds}秒`
                  : ''}
              </Typography>
              <IconButton
                onClick={goNext}
                sx={{ color: tvTheme.colors.textDark, bgcolor: 'rgba(255,255,255,0.9)', border: '2px solid #ccc' }}
                aria-label="次の画面"
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            </Box>
            <TVFrameLabel
              frameNumber={frames[previewIndex].id}
              frameTitle={frames[previewIndex].title}
              large
            />
            <TVFrameCard
              broadcast
              showBadge={false}
              allowOverflow={isTvActionFrame(frames[previewIndex]) || frames[previewIndex].kind === 'webGuide'}
              showQrOverlay={
                !isWeekly && shouldShowTvFrameQr(frames[previewIndex], broadcastMode)
              }
            >
              {renderFrameContent(frames[previewIndex], false)}
            </TVFrameCard>
            <Typography
              sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.85rem', color: tvTheme.colors.textMuted, fontWeight: 600 }}
            >
              ← → キーで放送フローを順送り
              {isWeekly
                ? ` · 合計目安 ${totalSeconds}秒`
                : ' · 朝・昼・夜は上部で変更'}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            mt: 3,
            pt: 2,
            borderTop: '2px solid rgba(255,255,255,0.5)',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: tvTheme.colors.textDark,
              fontWeight: 600,
              lineHeight: 1.6,
              bgcolor: 'rgba(255,255,255,0.75)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {footerFlow}
            {!isWeekly ? ' · 行動提案以外QR常設' : ''}
            {' · 放送仕様は未確定（会議比較用プロトタイプ）'}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default TVStoryboardPage;
